<template>
  <div class="table-wrapper">
    <table
      v-if="propostas && propostas.length"
      class="clean-table"
      style="margin-top: 1rem;"
    >
      <thead>
        <tr>
          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faFileContract"
                class="th-icon"
              />
              Número da proposta
            </span>
          </th>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faDollarSign"
                class="th-icon"
              />
              Valor
            </span>
          </th>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faCircleNotch"
                class="th-icon"
              />
              Status
            </span>
          </th>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faCircleNotch"
                class="th-icon"
              />
              Tipo
            </span>
          </th>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faUserTie"
                class="th-icon"
              />
              Responsável
            </span>
          </th>

          <th>
            <span class="th-content">
              <font-awesome-icon
                :icon="faUser"
                class="th-icon"
              />
              Pessoa
            </span>
          </th>

          <th class="action-col">
            <span class="th-content" />
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="proposta in propostas"
          :key="proposta.id"
        >
          <td class="titulo-td">
            {{ proposta.numeroProposta }}
          </td>
          <td>
            {{ formatCurrency(proposta.valor) }}
          </td>
          <td>
            {{ statusText(proposta.status) }}
          </td>
          <td>
            {{ proposta.tipo }}
          </td>
          <td>
            {{ proposta.responsavel || '-' }}
          </td>
          <td>
            {{ proposta.pessoa || '-' }}
          </td>

          <td class="actions">
            <font-awesome-icon
              :icon="faEye"
              class="action"
              @click="detalharProposta(proposta)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-else
      class="text-center my-4"
    >
      <em v-if="!carregandoPropostas">Nenhuma proposta encontrada para esta oportunidade.</em>
      <em v-else>Carregando propostas...</em>
    </div>
  </div>
</template>

<style>
/* Wrapper para scroll horizontal */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.titulo-td {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ----------------------
   TABLE BASE
------------------------- */
.clean-table {
  width: 100%;
  border-collapse: collapse;
  font-family: inherit;
  color: #333;
}

/* ----------------------
   TABLE HEAD
------------------------- */
.clean-table thead th {
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid #e2e2e2;
  font-size: 14px;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.th-icon {
  font-size: 16px;
  color: #777;
}

/* ----------------------
   TABLE BODY
------------------------- */
.clean-table tbody td {
  padding: 12px 8px;
  border-bottom: 1px solid #f1f1f1;
  font-size: 14px;
}

/* ----------------------
   ACTION COLUMN
------------------------- */
.action-col {
  width: 90px;
}

.action {
  cursor: pointer;
  color: #666;
  transition: 0.2s ease;
  font-size: 18px;
}

.action:hover {
  color: #000;
}

.action.edit:hover {
  color: #007bff;
}

.action.delete:hover {
  color: #dc3545;
}
</style>

<script>
import {
  faCircleNotch, faDollarSign, faEye, faFileContract, faUser, faUserTie,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import api from '@/app-modulo/comercial/oportunidades/shared/services/api'

export default {
  name: 'PropostasTabela',
  components: {
    FontAwesomeIcon,
  },
  props: {
    oportunidadeId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      faFileContract,
      faDollarSign,
      faUserTie,
      faEye,
      faUser,
      faCircleNotch,
      carregandoPropostas: false,
      carregandoDetalhes: false,
      propostas: [],
    }
  },
  watch: {
    oportunidadeId(newVal) {
      if (newVal) this.carregarPropostas()
    },
  },
  mounted() {
    if (this.oportunidadeId) this.carregarPropostas()
  },
  methods: {
    async carregarPropostas() {
      this.carregandoPropostas = true
      try {
        const res = await api.getDealProposals(this.oportunidadeId)
        this.propostas = res && res.data ? res.data : []
        this.emitirStats()
      } catch (e) {
        this.propostas = []
        this.emitirStats()
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar propostas', e)
      } finally {
        this.carregandoPropostas = false
      }
    },

    emitirStats() {
      const propostas = Array.isArray(this.propostas) ? this.propostas : []

      const getStatus = p => Number(
        p.status
        ?? p.statusId
        ?? p.statusProposta
        ?? p.Status
        ?? p.StatusId
        ?? 0,
      ) || 0

      const geraContrato = p => Boolean(
        p.geraContrato ?? p.GeraContrato ?? p.gera_contrato ?? false,
      )

      // Ajuste fino aqui se você quiser classificar diferente
      const isAprovada = s => [3, 8, 9].includes(s) // Aprovada / Finalizada / Aguardando Assinatura
      const isAguardandoEnvio = s => [1, 6].includes(s) // Rascunho / Checklist
      const isNaoAprovada = s => [4, 7, 5].includes(s) // Rejeitada / Não Aprovada / Expirada

      // "Abertas": tudo que não é encerrado/negativo (ajuste se precisar)
      const isAberta = s => ![8, 4, 7, 5].includes(s)

      const buildResumo = arr => {
        const resumo = {
          aprovadas: 0,
          aguardandoEnvio: 0,
          naoAprovadas: 0,
          total: arr.length,
          abertas: 0,
        }

        arr.forEach(p => {
          const s = getStatus(p)

          if (isAprovada(s)) resumo.aprovadas += 1
          else if (isAguardandoEnvio(s)) resumo.aguardandoEnvio += 1
          else if (isNaoAprovada(s)) resumo.naoAprovadas += 1

          if (isAberta(s)) resumo.abertas += 1
        })

        return resumo
      }

      const propostasResumo = buildResumo(propostas)

      const contratosArr = propostas.filter(geraContrato)
      const contratosResumo = buildResumo(contratosArr)

      // Pacote Contrato: pega o último nome encontrado (mais recente)
      const getPacoteNome = p => String(
        p.pacoteContratoNome
          ?? p.PacoteContratoNome
          ?? p.pacoteContrato
          ?? p.PacoteContrato
          ?? '',
      ).trim()

      const pacoteContratoNome = [...propostas]
        .reverse()
        .map(getPacoteNome)
        .find(n => n) || ''

      this.$emit('stats', {
        propostas: propostasResumo,
        contratos: {
          aprovadas: contratosResumo.aprovadas,
          aguardandoEnvio: contratosResumo.aguardandoEnvio,
          naoAprovadas: contratosResumo.naoAprovadas,
          total: contratosResumo.total,
        },
        pacoteContratoNome,
      })
    },

    detalharProposta(proposta) {
      this.$emit('detalhar-proposta', proposta)
      this.carregandoDetalhes = false
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '-'
      try {
        return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      } catch (e) {
        return value
      }
    },

    statusText(code) {
      switch (Number(code)) {
        case 1: return 'Rascunho'
        case 2: return 'Enviada'
        case 3: return 'Aprovada'
        case 4: return 'Rejeitada'
        case 5: return 'Expirada'
        case 6: return 'Checklist'
        case 7: return 'Não Aprovada'
        case 8: return 'Finalizada'
        case 9: return 'Aguardando Assinatura'
        default: return String(code)
      }
    },
  },
}
</script>
